import { useEffect, useRef, useCallback } from 'react';
import { debounce } from 'lodash';
import { useExperiments } from '@wix/yoshi-flow-editor';
import { OOI_EXPERIMENTS } from '@app/experiments';

type UsePostListVisibilityProps = {
  isLoading: boolean;
  isFirstRender: React.RefObject<boolean>;
  proGalleryRef: React.RefObject<HTMLDivElement>;
};

const DEBOUNCE_TIME = 1000;

export const usePostListVisibility = ({
  isLoading: isPostListLoading,
  proGalleryRef,
  isFirstRender,
}: UsePostListVisibilityProps) => {
  const isVisible = useRef(false);
  const { experiments } = useExperiments();
  const isScrollPostListToTopEnabled = experiments.enabled(OOI_EXPERIMENTS.SCROLL_POST_LIST_TO_TOP);

  const callback = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    isVisible.current = entry.intersectionRatio === 1;
  }, []);

  useEffect(() => {
    if (!proGalleryRef.current || !isScrollPostListToTopEnabled) {
      return;
    }

    const observer = new IntersectionObserver(debounce(callback, DEBOUNCE_TIME), { threshold: 1 });
    observer.observe(proGalleryRef.current);
    return () => observer.disconnect();
  }, [callback, proGalleryRef, isScrollPostListToTopEnabled]);

  const shouldScrollPostListToTop =
    !isVisible.current &&
    isScrollPostListToTopEnabled &&
    !isFirstRender.current &&
    !isPostListLoading;

  return shouldScrollPostListToTop;
};
